import React, { useState } from "react";
import { PlusCircle, Save, Trophy, CheckCircle, ChevronDown, ChevronUp, Trash2, Award, Minus } from "lucide-react";
import { useAwardsSettings } from "../../../hooks/useAwardsSettings";
import { useDivisions } from "../../../hooks/useDivisions";
import { useAgeGroups } from "../../../hooks/useAgeGroups";
import { useCompetitionCategories } from "../../../hooks/useCompetitionCategories";
import { useStyleCategories } from "../../../hooks/useStyleCategories";
import { AWARD_TYPE_TEMPLATES, getAllTemplateTypes, createCustomAwardFromTemplate } from "../../../constants/awardTypeTemplates";
import { CardHeader } from "../../../components/ui/CardHeader";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { ErrorAlert } from "../../../components/ui/ErrorAlert";

/**
 * Settings component for managing special/custom awards
 */
export function SpecialAwardsSettings() {
  const {
    customAwards,
    addCustomAward,
    updateCustomAward,
    removeCustomAward,
    toggleCustomAwardEnabled,
    saveAwards,
    loading,
    error,
    hasChanges,
    successMessage
  } = useAwardsSettings();

  // Fetch available divisions from pricing settings
  const { divisions, loading: divisionsLoading } = useDivisions();
  
  // Fetch available age groups from registration rules
  const { ageGroups, loading: ageGroupsLoading } = useAgeGroups();
  
  // Fetch available competition categories from registration rules
  const { competitionCategories, loading: competitionCategoriesLoading } = useCompetitionCategories();
  
  // Fetch available style categories from registration rules
  const { styleCategories, loading: styleCategoriesLoading } = useStyleCategories();

  const [expandedAwards, setExpandedAwards] = useState({});
  const [showAddMenu, setShowAddMenu] = useState(false);

  const toggleExpanded = (index) => {
    setExpandedAwards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleAddAwardType = (templateType) => {
    const newCustomAward = createCustomAwardFromTemplate(templateType);
    if (newCustomAward) {
      addCustomAward(newCustomAward);
      setExpandedAwards(prev => ({
        ...prev,
        [customAwards.length]: true
      }));
    }
    setShowAddMenu(false);
  };

  const handleUpdateAward = (index, field, value) => {
    const updatedAward = {
      ...customAwards[index],
      [field]: value
    };
    updateCustomAward(index, updatedAward);
  };

  const handleUpdateAwardConfig = (index, configField, value) => {
    const updatedAward = {
      ...customAwards[index],
      config: {
        ...customAwards[index].config,
        [configField]: value
      }
    };
    updateCustomAward(index, updatedAward);
  };

  // Handle division exclusion updates
  const handleDivisionExclusionChange = (awardIndex, division, isExcluded) => {
    const currentExclusions = customAwards[awardIndex].config.excludedDivisions || [];
    let newExclusions;
    
    if (isExcluded) {
      // Add division to exclusions if not already there
      newExclusions = currentExclusions.includes(division) 
        ? currentExclusions 
        : [...currentExclusions, division];
    } else {
      // Remove division from exclusions
      newExclusions = currentExclusions.filter(d => d !== division);
    }
    
    handleUpdateAwardConfig(awardIndex, 'excludedDivisions', newExclusions);
  };

  // Handle competition category exclusion updates
  const handleCompetitionCategoryExclusionChange = (awardIndex, category, isExcluded) => {
    const currentExclusions = customAwards[awardIndex].config.excludedCompetitionCategories || [];
    let newExclusions;
    
    if (isExcluded) {
      // Add category to exclusions if not already there
      newExclusions = currentExclusions.includes(category) 
        ? currentExclusions 
        : [...currentExclusions, category];
    } else {
      // Remove category from exclusions
      newExclusions = currentExclusions.filter(c => c !== category);
    }
    
    handleUpdateAwardConfig(awardIndex, 'excludedCompetitionCategories', newExclusions);
  };

  // Handle style category exclusion updates
  const handleStyleCategoryExclusionChange = (awardIndex, category, isExcluded) => {
    const currentExclusions = customAwards[awardIndex].config.excludedStyleCategories || [];
    let newExclusions;
    
    if (isExcluded) {
      // Add category to exclusions if not already there
      newExclusions = currentExclusions.includes(category) 
        ? currentExclusions 
        : [...currentExclusions, category];
    } else {
      // Remove category from exclusions
      newExclusions = currentExclusions.filter(c => c !== category);
    }
    
    handleUpdateAwardConfig(awardIndex, 'excludedStyleCategories', newExclusions);
  };

  // Handle splitting an age group for Greatest Hits
  const handleToggleSplitAgeGroup = (awardIndex, ageGroup, isSplit) => {
    const currentSplits = customAwards[awardIndex].config.splitAgeGroups || {};
    let newSplits;
    
    if (isSplit) {
      // Add age group to splits with two empty arrays (two columns)
      newSplits = {
        ...currentSplits,
        [ageGroup]: [[], []] // Two columns for splits
      };
    } else {
      // Remove age group from splits
      newSplits = { ...currentSplits };
      delete newSplits[ageGroup];
    }
    
    handleUpdateAwardConfig(awardIndex, 'splitAgeGroups', newSplits);
  };

  // Handle division selection for a split age group column
  const handleSplitAgeGroupDivisionChange = (awardIndex, ageGroup, columnIndex, division, isSelected) => {
    const currentSplits = customAwards[awardIndex].config.splitAgeGroups || {};
    const currentSplitsForAgeGroup = currentSplits[ageGroup] || [[], []];
    const currentDivisions = currentSplitsForAgeGroup[columnIndex] || [];
    let newDivisions;
    
    if (isSelected) {
      // Add division if not already selected
      newDivisions = currentDivisions.includes(division)
        ? currentDivisions
        : [...currentDivisions, division];
    } else {
      // Remove division from selections
      newDivisions = currentDivisions.filter(d => d !== division);
    }
    
    // Update the specific column for this age group
    const updatedSplitsForAgeGroup = [...currentSplitsForAgeGroup];
    updatedSplitsForAgeGroup[columnIndex] = newDivisions;
    
    const newSplits = {
      ...currentSplits,
      [ageGroup]: updatedSplitsForAgeGroup
    };
    
    handleUpdateAwardConfig(awardIndex, 'splitAgeGroups', newSplits);
  };

  const getTemplateForAward = (award) => {
    return AWARD_TYPE_TEMPLATES[Object.keys(AWARD_TYPE_TEMPLATES).find(
      key => AWARD_TYPE_TEMPLATES[key].typeId === award.templateType
    )];
  };

  // Check which templates are already added
  // For Greatest Hits, allow multiple instances (so admins can create specialized versions)
  // For other templates, only allow one instance per type
  const addedTemplateTypes = customAwards
    .filter(award => award.templateType !== 'greatestHits')
    .map(award => award.templateType);
  const availableTemplates = getAllTemplateTypes().filter(
    template => !addedTemplateTypes.includes(template.typeId)
  );

  if (loading && customAwards.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Special Awards Content */}
      <div className="card bg-base-100 border border-base-300">
        <div className="card-body p-6">
          <CardHeader
            icon={<Trophy />}
            title="Special Awards Configuration"
            description="Customize award types with your own branding and settings"
          />

          <ErrorAlert message={error} className="mt-4" />

          {successMessage && (
            <div className="alert alert-success mt-4">
              <CheckCircle className="w-5 h-5" />
              <span>{successMessage}</span>
            </div>
          )}

          <div className="divider mt-6">Award Types</div>

          {/* Custom Awards List */}
          <div className="space-y-4">
            {customAwards.length === 0 ? (
              <div className="text-center py-8 text-base-content/60">
                <Trophy className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>No special awards configured yet.</p>
                <p className="text-sm mt-1">Add award types below to get started.</p>
              </div>
            ) : (
              customAwards.map((award, index) => {
                const template = getTemplateForAward(award);
                const isExpanded = expandedAwards[index];

                return (
                  <div key={index} className="border border-base-300 rounded-lg overflow-hidden">
                    {/* Header */}
                    <div className="bg-base-200 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={award.enabled}
                            onChange={() => toggleCustomAwardEnabled(index)}
                            className="checkbox checkbox-sm"
                          />
                          <div className="flex-1">
                            <input
                              type="text"
                              value={award.displayName}
                              onChange={(e) => handleUpdateAward(index, 'displayName', e.target.value)}
                              className="input input-bordered input-sm w-full max-w-md font-semibold"
                              placeholder={template?.defaultName}
                            />
                            <p className="text-xs text-base-content/60 mt-1">
                              {template?.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleExpanded(index)}
                            className="btn btn-ghost btn-sm btn-square"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => removeCustomAward(index)}
                            className="btn btn-ghost btn-sm btn-square text-error"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Configuration */}
                    {isExpanded && (
                      <div className="p-4 space-y-4 bg-base-100">
                        {/* Variants Configuration (if supported) */}
                        {template?.supportsVariants && award.config.activeVariants && (
                          <div className="space-y-2">
                            <label className="label">
                              <span className="label-text font-semibold">Available Variants</span>
                            </label>
                            {template.defaultVariants.map(variant => (
                              <label key={variant.id} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={award.config.activeVariants.includes(variant.id)}
                                  onChange={(e) => {
                                    const newVariants = e.target.checked
                                      ? [...award.config.activeVariants, variant.id]
                                      : award.config.activeVariants.filter(v => v !== variant.id);
                                    handleUpdateAwardConfig(index, 'activeVariants', newVariants);
                                  }}
                                  className="checkbox checkbox-sm"
                                />
                                <div>
                                  <span className="text-sm font-medium">{variant.name}</span>
                                  <span className="text-xs text-base-content/60 ml-2">{variant.description}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}

                        {/* Winners Variant (if supported) */}
                        {template?.supportsWinners && (
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={award.config.enableWinnersVariant}
                                onChange={(e) => handleUpdateAwardConfig(index, 'enableWinnersVariant', e.target.checked)}
                                className="checkbox checkbox-sm"
                              />
                              <span className="text-sm font-semibold">Enable Winners Selection Variant</span>
                            </label>
                            {award.config.enableWinnersVariant && (
                              <input
                                type="text"
                                value={award.config.winnersDisplayName || ''}
                                onChange={(e) => handleUpdateAwardConfig(index, 'winnersDisplayName', e.target.value)}
                                className="input input-bordered input-sm w-full"
                                placeholder={`${award.displayName} - Winners`}
                              />
                            )}
                          </div>
                        )}

                        {/* Split Age Groups for Greatest Hits */}
                        {template?.typeId === 'greatestHits' && (
                          <div className="space-y-4 border-t border-base-300 pt-4">
                            <div className="bg-base-200 p-3 rounded-lg">
                              <p className="text-xs text-base-content/70">
                                <strong>Split Age Groups:</strong> All age groups are included by default. Check "Split" for any age group to show only specific divisions for that age group. 
                                This allows you to create specialized awards (e.g., "Teen Small Group Greatest Hits" shows only Small Group for Teen, while other age groups show all divisions).
                              </p>
                            </div>
                            
                            {ageGroupsLoading && (
                              <div className="text-sm text-base-content/60">Loading age groups...</div>
                            )}

                            {/* Age Group Split Configuration */}
                            {ageGroups.length > 0 && divisions.length > 0 && (
                              <div className="space-y-3">
                                <label className="label">
                                  <span className="label-text font-semibold">Age Group Splits</span>
                                  <span className="label-text-alt">Split specific age groups to show only selected divisions</span>
                                </label>
                                
                                <div className="space-y-3 max-h-96 overflow-y-auto border border-base-300 rounded-lg p-3">
                                  {ageGroups.map(ageGroup => {
                                    const splitAgeGroups = award.config.splitAgeGroups || {};
                                    const isSplit = ageGroup in splitAgeGroups;
                                    const splitColumns = splitAgeGroups[ageGroup] || [[], []];
                                    
                                    return (
                                      <div key={ageGroup} className="border-b border-base-300 pb-3 last:border-b-0 last:pb-0">
                                        {/* Age Group Split Toggle */}
                                        <label className="flex items-center gap-2 cursor-pointer mb-2">
                                          <input
                                            type="checkbox"
                                            checked={isSplit}
                                            onChange={(e) => handleToggleSplitAgeGroup(index, ageGroup, e.target.checked)}
                                            className="checkbox checkbox-sm"
                                          />
                                          <span className="text-sm font-semibold">Split {ageGroup} age group</span>
                                        </label>
                                        
                                        {/* Two Column Division Selection for Split Age Group */}
                                        {isSplit && (
                                          <div className="ml-6 mt-2 space-y-3">
                                            <span className="text-xs text-base-content/70 block mb-2">
                                              Select divisions for each split column:
                                            </span>
                                            <div className="grid grid-cols-2 gap-4">
                                              {/* Column 1 */}
                                              <div className="space-y-2">
                                                <label className="text-xs font-semibold text-base-content/80">
                                                  Column 1 Divisions:
                                                </label>
                                                <div className="space-y-1 border border-base-300 rounded-lg p-2 max-h-48 overflow-y-auto">
                                                  {divisions.map(division => {
                                                    const isSelected = splitColumns[0]?.includes(division) || false;
                                                    return (
                                                      <label key={`${division}-col1`} className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-1 rounded">
                                                        <input
                                                          type="checkbox"
                                                          checked={isSelected}
                                                          onChange={(e) => handleSplitAgeGroupDivisionChange(index, ageGroup, 0, division, e.target.checked)}
                                                          className="checkbox checkbox-xs"
                                                        />
                                                        <span className="text-xs">{division}</span>
                                                      </label>
                                                    );
                                                  })}
                                                </div>
                                                {splitColumns[0]?.length > 0 && (
                                                  <div className="text-xs text-base-content/60 mt-1">
                                                    Selected: {splitColumns[0].join(', ')}
                                                  </div>
                                                )}
                                              </div>
                                              
                                              {/* Column 2 */}
                                              <div className="space-y-2">
                                                <label className="text-xs font-semibold text-base-content/80">
                                                  Column 2 Divisions:
                                                </label>
                                                <div className="space-y-1 border border-base-300 rounded-lg p-2 max-h-48 overflow-y-auto">
                                                  {divisions.map(division => {
                                                    const isSelected = splitColumns[1]?.includes(division) || false;
                                                    return (
                                                      <label key={`${division}-col2`} className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-1 rounded">
                                                        <input
                                                          type="checkbox"
                                                          checked={isSelected}
                                                          onChange={(e) => handleSplitAgeGroupDivisionChange(index, ageGroup, 1, division, e.target.checked)}
                                                          className="checkbox checkbox-xs"
                                                        />
                                                        <span className="text-xs">{division}</span>
                                                      </label>
                                                    );
                                                  })}
                                                </div>
                                                {splitColumns[1]?.length > 0 && (
                                                  <div className="text-xs text-base-content/60 mt-1">
                                                    Selected: {splitColumns[1].join(', ')}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {!isSplit && (
                                          <div className="ml-6 text-xs text-base-content/60 italic">
                                            {ageGroup} will show all divisions
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Eligibility Criteria (for Studio Standout) */}
                        {template?.requiresEligibilityCheck && award.config.eligibilityCriteria && (
                          <div className="space-y-2">
                            <label className="label">
                              <span className="label-text font-semibold">Eligibility Requirements</span>
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="label">
                                  <span className="label-text text-xs">Min Total Routines</span>
                                </label>
                                <input
                                  type="number"
                                  value={award.config.eligibilityCriteria.minTotalRoutines}
                                  onChange={(e) => handleUpdateAwardConfig(index, 'eligibilityCriteria', {
                                    ...award.config.eligibilityCriteria,
                                    minTotalRoutines: parseInt(e.target.value)
                                  })}
                                  className="input input-bordered input-sm w-full"
                                  min="1"
                                />
                              </div>
                              <div>
                                <label className="label">
                                  <span className="label-text text-xs">Min Group Routines</span>
                                </label>
                                <input
                                  type="number"
                                  value={award.config.eligibilityCriteria.minGroupRoutines}
                                  onChange={(e) => handleUpdateAwardConfig(index, 'eligibilityCriteria', {
                                    ...award.config.eligibilityCriteria,
                                    minGroupRoutines: parseInt(e.target.value)
                                  })}
                                  className="input input-bordered input-sm w-full"
                                  min="1"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Division Exclusions */}
                        {divisions.length > 0 && (
                          <div className="space-y-2">
                            <label className="label">
                              <span className="label-text font-semibold">Exclude Divisions</span>
                              <span className="label-text-alt">Select divisions to exclude from this award</span>
                            </label>
                            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-base-300 rounded-lg p-3">
                              {divisions.map(division => {
                                const isExcluded = (award.config.excludedDivisions || []).includes(division);
                                return (
                                  <label key={division} className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-1 rounded">
                                    <input
                                      type="checkbox"
                                      checked={isExcluded}
                                      onChange={(e) => handleDivisionExclusionChange(index, division, e.target.checked)}
                                      className="checkbox checkbox-sm"
                                    />
                                    <span className="text-sm">{division}</span>
                                    {isExcluded && <Minus className="w-3 h-3 text-error ml-auto" />}
                                  </label>
                                );
                              })}
                            </div>
                            {(award.config.excludedDivisions || []).length > 0 && (
                              <div className="text-xs text-base-content/60">
                                Excluded: {(award.config.excludedDivisions || []).join(', ')}
                              </div>
                            )}
                          </div>
                        )}

                        {divisionsLoading && (
                          <div className="text-sm text-base-content/60">Loading divisions...</div>
                        )}

                        {/* Competition Category Exclusions - Only for highScoreByAge and highScoreByDivision */}
                        {(template?.typeId === 'highScoreByAge' || template?.typeId === 'highScoreByDivision') && (
                          <>
                            {competitionCategories.length > 0 && (
                              <div className="space-y-2 border-t border-base-300 pt-4">
                                <label className="label">
                                  <span className="label-text font-semibold">Exclude Competition Categories</span>
                                  <span className="label-text-alt">Select competition categories to exclude from this award</span>
                                </label>
                                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-base-300 rounded-lg p-3">
                                  {competitionCategories.map(category => {
                                    const isExcluded = (award.config.excludedCompetitionCategories || []).includes(category);
                                    return (
                                      <label key={category} className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-1 rounded">
                                        <input
                                          type="checkbox"
                                          checked={isExcluded}
                                          onChange={(e) => handleCompetitionCategoryExclusionChange(index, category, e.target.checked)}
                                          className="checkbox checkbox-sm"
                                        />
                                        <span className="text-sm">{category}</span>
                                        {isExcluded && <Minus className="w-3 h-3 text-error ml-auto" />}
                                      </label>
                                    );
                                  })}
                                </div>
                                {(award.config.excludedCompetitionCategories || []).length > 0 && (
                                  <div className="text-xs text-base-content/60">
                                    Excluded: {(award.config.excludedCompetitionCategories || []).join(', ')}
                                  </div>
                                )}
                              </div>
                            )}

                            {competitionCategoriesLoading && (
                              <div className="text-sm text-base-content/60">Loading competition categories...</div>
                            )}

                            {/* Style Category Exclusions */}
                            {styleCategories.length > 0 && (
                              <div className="space-y-2 border-t border-base-300 pt-4">
                                <label className="label">
                                  <span className="label-text font-semibold">Exclude Style Categories</span>
                                  <span className="label-text-alt">Select style categories to exclude from this award</span>
                                </label>
                                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-base-300 rounded-lg p-3">
                                  {styleCategories.map(category => {
                                    const isExcluded = (award.config.excludedStyleCategories || []).includes(category);
                                    return (
                                      <label key={category} className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-1 rounded">
                                        <input
                                          type="checkbox"
                                          checked={isExcluded}
                                          onChange={(e) => handleStyleCategoryExclusionChange(index, category, e.target.checked)}
                                          className="checkbox checkbox-sm"
                                        />
                                        <span className="text-sm">{category}</span>
                                        {isExcluded && <Minus className="w-3 h-3 text-error ml-auto" />}
                                      </label>
                                    );
                                  })}
                                </div>
                                {(award.config.excludedStyleCategories || []).length > 0 && (
                                  <div className="text-xs text-base-content/60">
                                    Excluded: {(award.config.excludedStyleCategories || []).join(', ')}
                                  </div>
                                )}
                              </div>
                            )}

                            {styleCategoriesLoading && (
                              <div className="text-sm text-base-content/60">Loading style categories...</div>
                            )}
                          </>
                        )}

                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Add Award Type Button */}
            {availableTemplates.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowAddMenu(!showAddMenu)}
                  className="btn btn-outline btn-block btn-sm"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Award Type
                </button>

                {/* Add Menu Dropdown */}
                {showAddMenu && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-base-100 border border-base-300 rounded-lg shadow-lg z-10">
                    <div className="p-2">
                      <p className="text-xs text-base-content/60 px-3 py-2">Select an award type to add:</p>
                      {availableTemplates.map(template => (
                        <button
                          key={template.typeId}
                          onClick={() => handleAddAwardType(template.typeId)}
                          className="w-full text-left px-3 py-2 hover:bg-base-200 rounded-lg transition-colors"
                        >
                          <div className="font-semibold text-sm">{template.defaultName}</div>
                          <div className="text-xs text-base-content/60">{template.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {availableTemplates.length === 0 && customAwards.length > 0 && (
              <div className="text-center py-4 text-sm text-base-content/60">
                All available award types have been added
              </div>
            )}
          </div>

          <div className="card-actions justify-end mt-6">
            <button
              onClick={saveAwards}
              disabled={!hasChanges || loading}
              className="btn btn-primary"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

